import { Inject, Injectable, Optional } from '@angular/core';
import { BehaviorSubject, defer, Observable, Subject, lastValueFrom, of } from 'rxjs';
import Shepherd from 'shepherd.js';
import { TOUR_BUILDERS } from './tour.tokens';
import { PersistedState, TourBuilder, TourKey, TourRuntimeOptions, TourStep } from './tour.types';
import { Logger } from '@core/services';

const log = new Logger('TourService ');

@Injectable({ providedIn: 'root' })
export class TourService {
  private tour?: Shepherd.Tour;
  private activeKey?: TourKey;
  private activeSteps: TourStep[] = [];

  private progressOn = true;
  private progressPlacement: NonNullable<TourRuntimeOptions['progressPlacement']> = 'after-footer';
  private pendingJumpIndex: number | null = null;

  private offFns: Array<() => void> = [];
  private progressMO?: MutationObserver;

  private readonly stepIndex$ = new BehaviorSubject<number>(-1);
  private readonly events$ = new Subject<{ type: string; payload?: unknown }>();

  private initToken = 0;
  private starting = false;
  private runId = 0;
  private chain: Promise<void> = Promise.resolve();

  constructor(@Optional() @Inject(TOUR_BUILDERS) private builders: TourBuilder[] = []) {}

  onStepIndex() {
    return this.stepIndex$.asObservable();
  }

  onEvents() {
    return this.events$.asObservable();
  }

  isActive(): boolean {
    return (this.tour as any)?.isActive?.() === true;
  }

  /**
   * Cancels the current tour if it is running (does not mark as completed).
   * Safe to call anytime.
   */
  cancelIfActive(): boolean {
    const active = this.isActive();
    if (active) {
      try {
        log.debug('cancelIfActive: canceling active tour');
        this.tour?.cancel();
      } catch (e) {
        log.warn('cancelIfActive: cancel error (ignored)', e);
      }
      return true;
    }
    return false;
  }

  /**
   * Observable variant to end any active tour (useful when composing).
   */
  endActive$(): Observable<void> {
    return defer(() => {
      this.cancelIfActive();
      return of(void 0);
    });
  }

  /**
   * Initialize the specified tour and start it (serialized).
   * Auto-resumes from the last step if persisted state matches `key`.
   *
   * Now also ensures any active tour is cancelled before starting a new one.
   *
   * @param key   Tour key (selects builder)
   * @param opts  Runtime options (progress, placement, default step opts)
   * @returns     Observable that emits `'complete' | 'cancel'` once
   */
  begin$(key: TourKey, opts: TourRuntimeOptions = {}): Observable<'complete' | 'cancel'> {
    return new Observable(sub => {
      this.chain = this.chain
        .then(async () => {
          log.debug('begin$: queued', { key, opts });

          // ensure nothing is blocking
          this.cancelIfActive();

          // optional extra safety: remove any orphan DOM
          const removed = this.hardCleanupShepherdDom();
          if (removed) {
            log.debug('begin$: pre-cleanup orphan DOM removed', removed);
          }

          await lastValueFrom(this.init$(key, opts));
          const result = await lastValueFrom(this.start$());
          log.debug('begin$: finished', { key, result });
          sub.next(result);
          sub.complete();
        })
        .catch(err => {
          log.error('begin$: error', err);
          sub.error(err);
        });
    });
  }

  /**
   * Initialize the Shepherd tour for a given key.
   * Does not start; pair with `start$()`.
   *
   * Behavior:
   * - Cancels/destroys any live tour
   * - Removes orphan Shepherd DOM
   * - Builds steps from the matching builder
   * - Honors resume (skips steps with `skipOnResume` if resuming)
   */
  init$(key: TourKey, opts: TourRuntimeOptions = {}): Observable<void> {
    return defer(() => {
      const token = ++this.initToken;
      const runId = ++this.runId;
      log.debug('init$: start', { key, token, runId });

      try {
        this.tour?.cancel();
      } catch {}

      this.destroyTour();

      const removed = this.hardCleanupShepherdDom();
      if (removed) {
        log.debug('init$: orphan DOM removed', removed);
      }

      const builder = this.builders.find(b => b.key === key);
      if (!builder) {
        throw new Error(`Tour builder not found: ${key}`);
      }

      const {
        progress = true,
        progressPlacement = 'after-footer',
        defaultStepOptions = {
          cancelIcon: { enabled: false } as any,
          scrollTo: { behavior: 'smooth', block: 'center' } as any,
          canClickTarget: true as any
        }
      } = opts;

      this.progressOn = progress;
      this.progressPlacement = progressPlacement;
      this.activeKey = key;

      const state = this.readState();
      this.pendingJumpIndex =
        state && state.key === key && state.stepIndex > 0 ? state.stepIndex : null;
      log.debug('init$: pendingJumpIndex', this.pendingJumpIndex);

      const built = builder.buildSteps();

      // optional: skip "welcome" on resume if steps carry skipOnResume
      let steps = built;
      if (this.pendingJumpIndex != null) {
        const skippedBefore = built
          .slice(0, this.pendingJumpIndex)
          .filter(s => (s as any).skipOnResume).length;
        steps = built.filter(s => !(s as any).skipOnResume);

        if (skippedBefore) {
          this.pendingJumpIndex = Math.max(0, this.pendingJumpIndex - skippedBefore);
        }

        log.debug('init$: filtered for resume', { skippedBefore, newJump: this.pendingJumpIndex });
      }

      this.activeSteps = steps;

      if (token !== this.initToken) {
        log.debug('init$: aborted (superseded by newer init)', { token, current: this.initToken });
        return Promise.resolve();
      }

      this.tour = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: { ...(defaultStepOptions || {}), ...(builder.defaults || {}) } as any
      });

      this.activeSteps.forEach(s => this.tour!.addStep(s as any));

      // cancel self if a newer run starts before first show
      const killIfStale = () => {
        if (runId !== this.runId) {
          log.debug('init$: killIfStale cancel', { runId, current: this.runId });
          try {
            this.tour?.cancel();
          } catch {}
        }
      };

      this.tour.on('show' as any, killIfStale as any);

      this.wireEvents();
      log.debug('init$: done', { key, steps: this.activeSteps.length });
      return Promise.resolve();
    });
  }

  /**
   * Starts the initialized tour.
   * - No-op if not initialized
   * - Jumps to persisted step (if any) on first show
   *
   * @returns Observable that emits `'complete' | 'cancel'` once
   */
  start$(): Observable<'complete' | 'cancel'> {
    return new Observable(sub => {
      if (!this.tour) {
        return sub.error(new Error('Tour not initialized. Call init$() first.'));
      }

      const isActive = (this.tour as any).isActive?.() === true;
      if (isActive || this.starting) {
        log.debug('start$: ignored (active/starting)', { isActive, starting: this.starting });
        sub.complete();
        return;
      }

      this.starting = true;

      const removed = this.hardCleanupShepherdDom();
      if (removed) {
        log.debug('start$: orphan DOM removed', removed);
      }

      const finalize = () => {
        this.starting = false;
      };

      const onComplete = () => {
        log.debug('event: complete');
        sub.next('complete');
        sub.complete();
        finalize();
      };

      const onCancel = () => {
        log.debug('event: cancel');
        sub.next('cancel');
        sub.complete();
        finalize();
      };

      const onFirstShow = () => {
        this.tour?.off('show' as any, onFirstShow as any);
        const jumpTo = this.pendingJumpIndex;
        this.pendingJumpIndex = null;

        if (jumpTo != null && this.tour) {
          const total = this.tour.steps.length;
          const target = Math.min(Math.max(jumpTo, 0), total - 1);
          log.debug('start$: jumping to index', { jumpTo: target, total });

          for (let i = 0; i < target; i++) {
            this.tour.next();
          }
        }
      };

      this.tour.on('show' as any, onFirstShow as any);

      // bind via local helper (tracks off() cleanup)
      const add = (event: string, handler: (...a: any[]) => void) => {
        this.tour!.on(event as any, handler as any);
        this.offFns.push(() => this.tour?.off(event as any, handler as any));
      };

      add('complete', onComplete);
      add('cancel', onCancel);

      this.tour.start();
      log.debug('start$: started');

      return () => {
        this.offFns.forEach(fn => fn());
        this.offFns = [];
        this.disconnectProgressObserver();
        finalize();
        log.debug('start$: teardown complete');
      };
    });
  }

  cancel(): void {
    log.debug('api: cancel');
    this.tour?.cancel();
  }

  complete(): void {
    log.debug('api: complete');
    this.tour?.complete();
  }

  private wireEvents(): void {
    if (!this.tour) {
      log.debug('wireEvents: no tour');
      return;
    }

    const total = this.tour.steps.length;
    log.debug('wireEvents: bind', { total });

    const onShow = (evt: { step: any }) => {
      const step = evt.step;
      const index = this.tour!.steps.indexOf(step);

      this.stepIndex$.next(index);
      this.persist({ key: this.activeKey!, stepIndex: index });
      this.events$.next({ type: 'show', payload: { index, total } });
      log.debug('event: show', { index, total, id: step?.id });

      if (this.progressOn) {
        this.scheduleProgressRender(step, index, total);
      }
    };

    const onComplete = () => {
      this.events$.next({ type: 'complete' });
      this.clearPersisted();
      this.destroyTour();
      log.debug('wireEvents: complete → destroyed');
    };

    const onCancel = () => {
      this.events$.next({ type: 'cancel' });
      // keep persisted state for resume
      this.destroyTour();
      log.debug('wireEvents: cancel → destroyed');
    };

    const add = (event: string, handler: (...a: any[]) => void) => {
      this.tour!.on(event as any, handler as any);
      this.offFns.push(() => this.tour?.off(event as any, handler as any));
    };

    add('show', onShow);
    add('complete', onComplete);
    add('cancel', onCancel);
  }

  private scheduleProgressRender(step: any, index: number, total: number): void {
    this.disconnectProgressObserver();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.stepIndex$.value !== index) {
          return;
        }

        const container = this.getStepElement(step);

        if (!container) {
          return;
        }

        this.renderProgressInto(container, index + 1, total);
        this.observeProgress(container, index, total);
      });
    });
  }

  private getStepElement(step: any): HTMLElement | null {
    const el = step?.getElement?.();

    if (el) {
      return el as HTMLElement;
    }

    const id = step?.id ?? step?.options?.id;

    if (id) {
      const byId = document.querySelector(
        `.shepherd-element[data-shepherd-step-id="${id}"]`
      ) as HTMLElement | null;

      if (byId) {
        return byId;
      }
    }

    const all = Array.from(document.querySelectorAll('.shepherd-element')) as HTMLElement[];
    return all.length ? all[all.length - 1] : null;
  }

  private renderProgressInto(container: HTMLElement, current: number, total: number): void {
    const content = container.querySelector('.shepherd-content') as HTMLElement | null;
    const footer = container.querySelector('.shepherd-footer') as HTMLElement | null;

    if (!content) {
      return;
    }

    container.querySelectorAll('.tour-progress').forEach(n => n.remove());
    const pct = Math.max(0, Math.min(100, Math.round((current / total) * 100)));

    const bar = document.createElement('div');
    bar.className = 'tour-progress';
    bar.setAttribute('aria-label', `Progress ${pct}%`);
    bar.innerHTML = `
      <div class="tour-progress-track">
        <div class="tour-progress-fill" style="width:${pct}%"></div>
      </div>
      <div class="tour-progress-label">${current}/${total}</div>
    `;

    switch (this.progressPlacement) {
      case 'top':
        content.prepend(bar);
        break;
      case 'before-footer':
        footer ? footer.before(bar) : content.append(bar);
        break;
      case 'after-footer':
      default:
        footer ? footer.after(bar) : content.append(bar);
        break;
    }
  }

  private observeProgress(container: HTMLElement, index: number, total: number): void {
    this.progressMO = new MutationObserver(() => {
      if (this.stepIndex$.value !== index || !document.contains(container)) {
        this.disconnectProgressObserver();
        return;
      }

      if (!container.querySelector('.tour-progress')) {
        this.renderProgressInto(container, index + 1, total);
      }
    });
    this.progressMO.observe(container, { childList: true, subtree: true });
  }

  private disconnectProgressObserver(): void {
    if (this.progressMO) {
      this.progressMO.disconnect();
      this.progressMO = undefined;
    }
  }

  private hardCleanupShepherdDom(): { elements: number; overlays: number } | null {
    try {
      const element = Array.from(document.querySelectorAll('.shepherd-element'));
      const overlay = Array.from(document.querySelectorAll('.shepherd-modal-overlay-container'));

      element.forEach(n => n.parentElement?.removeChild(n));
      overlay.forEach(n => n.parentElement?.removeChild(n));

      return element.length || overlay.length
        ? { elements: element.length, overlays: overlay.length }
        : null;
    } catch {
      return null;
    }
  }

  private persist(data: PersistedState): void {
    try {
      localStorage.setItem('tour:state', JSON.stringify(data));
    } catch {}
  }

  private readState(): PersistedState | null {
    try {
      const raw = localStorage.getItem('tour:state');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearPersisted(): void {
    try {
      localStorage.removeItem('tour:state');
    } catch {}
  }

  private destroyTour(): void {
    this.offFns.forEach(fn => fn());
    this.offFns = [];
    this.disconnectProgressObserver();
    this.tour = undefined;
    this.stepIndex$.next(-1);
    this.starting = false;
  }
}
